import React, { useState, useCallback } from 'react';
import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';

export interface ZoomableImageProps {
  src: string;
  alt?: string;
  width?: string | number;
  adminOverlay?: boolean;
  onReplace?: (file: File) => void;
  label?: string; // semantic name
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ src, alt = '', width = '100%', adminOverlay = true, onReplace, label }) => {
  const [open, setOpen] = useState(false);
  const { canUploadImages } = useAuth();
  const { darkMode } = useTheme();
  const canUpload = Boolean(onReplace) && canUploadImages;

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && onReplace) onReplace(f);
  }, [onReplace]);

  return (
    React.createElement(Box, { sx: { position: 'relative', width, cursor: 'zoom-in' }, onClick: handleOpen },
      React.createElement('img', { src, alt, style: { width: '100%', height: 'auto', display: 'block' } }),
      canUpload && adminOverlay && React.createElement(Box, {
        sx: {
          position: 'absolute', 
          top: 8, 
          right: 8, 
          background: darkMode ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.6)', 
          color: tokens.colors.light.text, 
          px: 1.5, 
          py: 0.5,
          borderRadius: 1, 
          fontSize: 12, 
          display: 'flex', 
          gap: 1, 
          alignItems: 'center'
        }, onClick: (e: React.MouseEvent) => e.stopPropagation()
      },
        React.createElement('label', { style: { cursor: 'pointer' } }, '📷',
          React.createElement('input', { type: 'file', style: { display: 'none' }, accept: 'image/*', onChange: handleFileChange })
        ),
        React.createElement(Typography, { variant: 'caption', sx: { color: tokens.colors.light.text } }, label || 'Bild')
      ),
      React.createElement(Dialog, { 
        open, 
        onClose: handleClose,
        onKeyDown: (e: React.KeyboardEvent) => e.key === 'Escape' && handleClose(),
        maxWidth: 'lg',
        PaperProps: { 
          sx: { 
            bgcolor: darkMode ? tokens.colors.dark.background : tokens.colors.light.background,
            backgroundImage: 'none'
          } 
        }
      },
        React.createElement(DialogContent, { 
          sx: { 
            p: 0, 
            bgcolor: darkMode ? tokens.colors.dark.background : tokens.colors.light.background, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          } 
        },
          React.createElement('img', { 
            src, 
            alt, 
            style: { width: '100%', height: 'auto', display: 'block', maxHeight: '90vh', objectFit: 'contain', cursor: 'pointer' }, 
            onClick: handleClose
          })
        )
      )
    )
  );
};

export default ZoomableImage;
